import React, { useEffect, useMemo, useState } from "react";
import { Upload, Search, CheckCircle2, XCircle, Youtube, Loader2, User, Zap, ExternalLink, Link2, SkipForward } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { CATEGORIES as CATS } from "../lib/categories";

const CATEGORIES = CATS.map((c) => ({ value: c.key, label: `${c.emoji} ${c.label}` }));

/**
 * AdminBatchImportPanel
 * Permet à l'admin de coller 10 à 50 URLs YouTube/TikTok/Vimeo et de les importer
 * en masse pour un artiste sélectionné.
 */
export const AdminBatchImportPanel = () => {
  const [allArtists, setAllArtists] = useState([]);
  const [artistQ, setArtistQ] = useState("");
  const [selectedArtist, setSelectedArtist] = useState(null);
  const [urlsText, setUrlsText] = useState("");
  const [category, setCategory] = useState("music");
  const [genre, setGenre] = useState("");
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);

  // Mode toggle + Channel URL
  const [mode, setMode] = useState("urls"); // "urls" | "channel"
  const [channelUrl, setChannelUrl] = useState("");
  const [maxVideos, setMaxVideos] = useState(20);

  useEffect(() => {
    api.get("/admin/users", { params: { role: "artist", limit: 1000 } })
      .then(({ data }) => setAllArtists(data))
      .catch(() => setAllArtists([]));
  }, []);

  const filteredArtists = useMemo(() => {
    const q = artistQ.trim().toLowerCase();
    if (!q) return allArtists.slice(0, 30);
    return allArtists.filter((a) =>
      (a.name || "").toLowerCase().includes(q) ||
      (a.email || "").toLowerCase().includes(q) ||
      (a.city || "").toLowerCase().includes(q)
    ).slice(0, 50);
  }, [artistQ, allArtists]);

  const urlList = useMemo(() => {
    return urlsText
      .split(/\r?\n/)
      .map((l) => l.trim())
      .filter((l) => l.length > 0);
  }, [urlsText]);

  const launchImport = async () => {
    if (!selectedArtist) { toast.error("Sélectionne un artiste"); return; }

    setRunning(true);
    setResult(null);
    try {
      let data;
      if (mode === "channel") {
        if (!channelUrl.trim()) { toast.error("URL de chaîne requise"); setRunning(false); return; }
        const resp = await api.post(
          `/admin/users/${selectedArtist.user_id}/tracks/import-channel`,
          { channel_url: channelUrl.trim(), category, genre, max: maxVideos }
        );
        data = resp.data;
      } else {
        if (urlList.length === 0) { toast.error("Colle au moins une URL"); setRunning(false); return; }
        const resp = await api.post(
          `/admin/users/${selectedArtist.user_id}/tracks/batch`,
          { urls: urlList, category, genre }
        );
        data = resp.data;
      }
      setResult(data);
      if (data.summary.succeeded > 0) {
        toast.success(`${data.summary.succeeded} vidéo${data.summary.succeeded > 1 ? "s" : ""} ajoutée${data.summary.succeeded > 1 ? "s" : ""} à ${selectedArtist.name} ✨`);
      }
      if (data.summary.failed > 0) {
        toast.error(`${data.summary.failed} lien${data.summary.failed > 1 ? "s" : ""} non reconnu${data.summary.failed > 1 ? "s" : ""}`);
      }
      if (data.summary.skipped > 0) {
        toast(`${data.summary.skipped} déjà importé${data.summary.skipped > 1 ? "s" : ""} (ignoré${data.summary.skipped > 1 ? "s" : ""})`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.error || e?.response?.data?.detail || "Erreur lors de l'import");
    } finally {
      setRunning(false);
    }
  };

  const resetAll = () => {
    setUrlsText("");
    setResult(null);
  };

  return (
    <div className="space-y-5" data-testid="admin-batch-import-panel">
      <div className="rounded-2xl border border-white/10 bg-gradient-to-br from-brand-red/5 via-brand-orange/5 to-brand-yellow/5 p-6">
        <div className="flex items-center gap-2 mb-1">
          <Zap size={16} className="text-brand-orange" />
          <p className="text-[10px] uppercase tracking-widest text-brand-orange font-bold">Import rapide</p>
        </div>
        <h2 className="font-display text-2xl md:text-3xl font-black mb-1">Importer plusieurs vidéos d'un coup.</h2>
        <p className="text-sm text-white/60">
          Colle jusqu'à 50 URLs YouTube / TikTok / Vimeo / Instagram — le backend détecte le provider,
          extrait l'ID et crée les tracks au nom de l'artiste sélectionné.
        </p>
      </div>

      <div className="grid lg:grid-cols-[360px_1fr] gap-5">
        {/* ARTIST PICKER */}
        <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-4">
          <div className="flex items-center gap-2 mb-3">
            <User size={14} className="text-brand-orange" />
            <h3 className="font-display text-lg font-black">Artiste cible</h3>
          </div>

          {selectedArtist ? (
            <div className="rounded-xl border border-brand-orange/30 bg-brand-orange/5 p-3 mb-3 flex items-center gap-3" data-testid="batch-selected-artist">
              {selectedArtist.picture ? (
                <img src={selectedArtist.picture} alt="" className="w-11 h-11 rounded-full object-cover" />
              ) : (
                <div className="w-11 h-11 rounded-full bg-gradient-to-br from-brand-red to-brand-orange flex items-center justify-center font-black">
                  {(selectedArtist.name || "?").charAt(0).toUpperCase()}
                </div>
              )}
              <div className="min-w-0 flex-1">
                <p className="font-semibold truncate">{selectedArtist.name}</p>
                <p className="text-[11px] text-white/50 truncate">
                  {selectedArtist.country || "—"} · {selectedArtist.tracks_count || 0} tracks
                </p>
              </div>
              <button
                onClick={() => setSelectedArtist(null)}
                className="text-xs text-white/50 hover:text-white"
                data-testid="batch-clear-artist"
              >
                Changer
              </button>
            </div>
          ) : (
            <>
              <div className="relative mb-2">
                <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
                <input
                  value={artistQ}
                  onChange={(e) => setArtistQ(e.target.value)}
                  placeholder="Rechercher un artiste…"
                  className="w-full rounded-lg bg-white/[0.03] border border-white/10 pl-8 pr-3 py-2 text-sm focus:outline-none focus:border-brand-orange/60"
                  data-testid="batch-artist-search"
                />
              </div>
              <ul className="max-h-72 overflow-y-auto space-y-1 pr-1">
                {filteredArtists.map((a) => (
                  <li key={a.user_id}>
                    <button
                      onClick={() => setSelectedArtist(a)}
                      className="w-full flex items-center gap-2 p-2 rounded-lg hover:bg-white/[0.05] text-left transition-colors"
                      data-testid={`batch-artist-pick-${a.user_id}`}
                    >
                      {a.picture ? (
                        <img src={a.picture} alt="" className="w-8 h-8 rounded-full object-cover shrink-0" />
                      ) : (
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-brand-red to-brand-orange flex items-center justify-center font-black text-xs shrink-0">
                          {(a.name || "?").charAt(0).toUpperCase()}
                        </div>
                      )}
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold truncate">{a.name}</p>
                        <p className="text-[10px] text-white/40 truncate">
                          {a.country || "—"} · {a.tracks_count || 0} tracks
                        </p>
                      </div>
                    </button>
                  </li>
                ))}
                {filteredArtists.length === 0 && (
                  <li className="text-xs text-white/40 text-center py-4">Aucun artiste trouvé.</li>
                )}
              </ul>
            </>
          )}
        </div>

        {/* URLs + options */}
        <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-4 space-y-4">
          {/* Mode toggle */}
          <div className="flex gap-1 p-1 bg-black/30 rounded-xl border border-white/10" data-testid="batch-mode-toggle">
            <button
              type="button"
              onClick={() => setMode("urls")}
              className={`flex-1 inline-flex items-center justify-center gap-1.5 text-xs font-bold uppercase tracking-wider px-3 py-2 rounded-lg transition-colors ${
                mode === "urls" ? "bg-brand-orange/20 text-brand-orange" : "text-white/50 hover:text-white"
              }`}
              data-testid="batch-mode-urls"
            >
              <Youtube size={12} /> URLs manuelles
            </button>
            <button
              type="button"
              onClick={() => setMode("channel")}
              className={`flex-1 inline-flex items-center justify-center gap-1.5 text-xs font-bold uppercase tracking-wider px-3 py-2 rounded-lg transition-colors ${
                mode === "channel" ? "bg-brand-red/20 text-brand-red" : "text-white/50 hover:text-white"
              }`}
              data-testid="batch-mode-channel"
            >
              <Link2 size={12} /> Chaîne YouTube
            </button>
          </div>

          {mode === "urls" ? (
            <>
              <div className="flex items-center gap-2">
                <Youtube size={14} className="text-brand-red" />
                <h3 className="font-display text-lg font-black">URLs à importer</h3>
                <span className="ml-auto text-[11px] text-white/50" data-testid="batch-url-count">
                  {urlList.length}/50
                </span>
              </div>

              <textarea
                value={urlsText}
                onChange={(e) => setUrlsText(e.target.value)}
                placeholder={`Colle une URL par ligne, ex :\nhttps://www.youtube.com/watch?v=dQw4w9WgXcQ\nhttps://www.tiktok.com/@user/video/1234567890\nhttps://vimeo.com/123456`}
                rows={10}
                className="w-full rounded-lg bg-black/30 border border-white/10 px-3 py-3 text-sm font-mono focus:outline-none focus:border-brand-orange/60 resize-y"
                data-testid="batch-urls-textarea"
              />
            </>
          ) : (
            <>
              <div className="flex items-center gap-2">
                <Link2 size={14} className="text-brand-red" />
                <h3 className="font-display text-lg font-black">Chaîne YouTube à scraper</h3>
              </div>

              <input
                value={channelUrl}
                onChange={(e) => setChannelUrl(e.target.value)}
                placeholder="https://youtube.com/@BigMJOfficiel  ou  /channel/UC…"
                className="w-full rounded-lg bg-black/30 border border-white/10 px-3 py-3 text-sm font-mono focus:outline-none focus:border-brand-red/60"
                data-testid="batch-channel-url"
              />
              <div className="flex items-center gap-3 text-xs">
                <label className="text-white/60">Max vidéos à importer :</label>
                <input
                  type="number"
                  min="1"
                  max="50"
                  value={maxVideos}
                  onChange={(e) => setMaxVideos(Math.max(1, Math.min(50, parseInt(e.target.value) || 20)))}
                  className="w-20 rounded-lg bg-white/[0.03] border border-white/10 px-2 py-1.5 text-sm focus:outline-none focus:border-brand-orange/60"
                  data-testid="batch-channel-max"
                />
                <span className="text-white/40">(max 50)</span>
              </div>
              <p className="text-[10px] text-white/40 leading-relaxed">
                🔍 Le backend résout le channel_id, lit le flux RSS public (~15 dernières vidéos),
                puis importe tout en évitant les doublons (tracks déjà en base ignorées).
                Aucune clé API YouTube requise.
              </p>
            </>
          )}

          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-[10px] uppercase tracking-widest text-white/50 mb-1 font-bold">Catégorie</label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="w-full rounded-lg bg-white/[0.03] border border-white/10 px-3 py-2 text-sm focus:outline-none focus:border-brand-orange/60"
                data-testid="batch-category"
              >
                {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-widest text-white/50 mb-1 font-bold">Genre (optionnel)</label>
              <input
                value={genre}
                onChange={(e) => setGenre(e.target.value)}
                placeholder="Rap gasy, Salegy, Afrobeats…"
                className="w-full rounded-lg bg-white/[0.03] border border-white/10 px-3 py-2 text-sm focus:outline-none focus:border-brand-orange/60"
                data-testid="batch-genre"
              />
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2 pt-2">
            <button
              onClick={launchImport}
              disabled={running || !selectedArtist || (mode === "urls" ? urlList.length === 0 : !channelUrl.trim())}
              className="btn-primary inline-flex items-center gap-2 disabled:opacity-50"
              data-testid="batch-import-btn"
            >
              {running
                ? <><Loader2 size={14} className="animate-spin" /> Import en cours…</>
                : mode === "channel"
                  ? <><Link2 size={14} /> Scraper la chaîne</>
                  : <><Upload size={14} /> Importer {urlList.length > 0 ? `(${urlList.length})` : ""}</>}
            </button>
            {(urlsText || channelUrl || result) && (
              <button onClick={() => { resetAll(); setChannelUrl(""); }} className="btn-ghost text-sm" data-testid="batch-reset-btn">
                Reset
              </button>
            )}
            {!selectedArtist && (
              <p className="text-xs text-white/40 ml-auto">← Choisis d'abord un artiste</p>
            )}
          </div>

          {/* RESULTS */}
          {result && (
            <div className="mt-3 rounded-xl border border-white/10 bg-black/30 p-4 space-y-3" data-testid="batch-result">
              <div className="flex flex-wrap items-center gap-3">
                {result.channel_title && (
                  <div className="inline-flex items-center gap-2 text-brand-red">
                    <Link2 size={14} />
                    <span className="font-bold truncate max-w-[220px]" title={result.channel_title}>{result.channel_title}</span>
                  </div>
                )}
                <div className="inline-flex items-center gap-2 text-emerald-400">
                  <CheckCircle2 size={16} />
                  <span className="font-bold">{result.summary.succeeded}</span>
                  <span className="text-sm text-white/60">créé{result.summary.succeeded > 1 ? "s" : ""}</span>
                </div>
                {result.summary.skipped > 0 && (
                  <div className="inline-flex items-center gap-2 text-white/60">
                    <SkipForward size={16} />
                    <span className="font-bold">{result.summary.skipped}</span>
                    <span className="text-sm">ignoré{result.summary.skipped > 1 ? "s" : ""}</span>
                  </div>
                )}
                {result.summary.failed > 0 && (
                  <div className="inline-flex items-center gap-2 text-red-400">
                    <XCircle size={16} />
                    <span className="font-bold">{result.summary.failed}</span>
                    <span className="text-sm text-white/60">échec{result.summary.failed > 1 ? "s" : ""}</span>
                  </div>
                )}
                <span className="text-xs text-white/40 ml-auto">sur {result.summary.total} lien{result.summary.total > 1 ? "s" : ""}</span>
              </div>

              {result.created?.length > 0 && (
                <div>
                  <p className="text-[10px] uppercase tracking-widest text-emerald-400 font-bold mb-1">Ajoutés</p>
                  <ul className="space-y-1 max-h-48 overflow-y-auto">
                    {result.created.map((c) => (
                      <li key={c.track_id} className="flex items-center gap-2 text-xs text-white/70" data-testid={`batch-created-${c.track_id}`}>
                        <CheckCircle2 size={12} className="text-emerald-400 shrink-0" />
                        <span className="truncate flex-1">{c.title}</span>
                        <span className="text-[10px] uppercase text-brand-orange">{c.provider}</span>
                        <a href={c.url} target="_blank" rel="noreferrer" className="text-white/40 hover:text-brand-orange">
                          <ExternalLink size={11} />
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {result.failed?.length > 0 && (
                <div>
                  <p className="text-[10px] uppercase tracking-widest text-red-400 font-bold mb-1">Échecs</p>
                  <ul className="space-y-1 max-h-32 overflow-y-auto">
                    {result.failed.map((f, i) => (
                      <li key={i} className="flex items-center gap-2 text-xs text-white/60">
                        <XCircle size={12} className="text-red-400 shrink-0" />
                        <span className="truncate flex-1 font-mono">{f.url}</span>
                        <span className="text-[10px] text-red-300">{f.error}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          <p className="text-[10px] text-white/30 leading-relaxed pt-1">
            Providers supportés : YouTube, Vimeo, TikTok, Dailymotion, SoundCloud, Instagram, Facebook.
            <br />
            🎵 <strong className="text-brand-yellow">TikTok : titres, auteurs et miniatures récupérés automatiquement</strong> via l'oEmbed officiel — plus besoin de renommer à la main.
            YouTube : miniatures HD auto-générées depuis l'ID.
          </p>
        </div>
      </div>
    </div>
  );
};

export default AdminBatchImportPanel;
